import React, { createContext, useContext, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "./api";
import { useAuth } from "./AuthContext";

/**
 * Provides:
 *  - getTier(type, id) -> "gold"|"blue"|"red"|null  (current user's preference)
 *  - getAdminTier(type, id) -> same for the admin's public picks
 *  - setTier(type, id, tier)  cycles or sets
 *  - clearTier(type, id)
 *
 * Tier colours:
 *   gold = #F5C842  → favourite
 *   blue = #60A5FA  → like a lot
 *   red  = #EF4444  → don't like
 */
const FavCtx = createContext({});

export const TIER_COLOURS = {
    gold: "#F5C842",
    blue: "#60A5FA",
    red:  "#EF4444",
};

export const TIER_LABELS = {
    gold: "Favourite",
    blue: "Like A Lot",
    red:  "Don't Like",
};

export const FavouritesProvider = ({ children }) => {
    const qc = useQueryClient();
    const { user } = useAuth();
    const isAuthed = !!user;

    const { data: mine = [] } = useQuery({
        queryKey: ["my-favourites", user?.id || null],
        queryFn: () => api.get("/favourites").then((r) => r.data),
        enabled: isAuthed,
    });
    const { data: adminFavs = [] } = useQuery({
        queryKey: ["admin-favourites"],
        queryFn: () => api.get("/favourites/admin").then((r) => r.data),
        staleTime: 60 * 1000,
    });

    const setMut = useMutation({
        mutationFn: (body) => api.put("/favourites", body).then((r) => r.data),
        onMutate: async (vars) => {
            await qc.cancelQueries({ queryKey: ["my-favourites", user?.id || null] });
            const prev = qc.getQueryData(["my-favourites", user?.id || null]);
            qc.setQueryData(["my-favourites", user?.id || null], (old = []) => {
                const filtered = old.filter((f) => !(f.target_type === vars.target_type && f.target_id === vars.target_id));
                return [...filtered, { ...vars }];
            });
            return { prev };
        },
        onError: (_e, _v, ctx) => {
            if (ctx?.prev != null) qc.setQueryData(["my-favourites", user?.id || null], ctx.prev);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: ["my-favourites", user?.id || null] });
            // Admin favs may change too if current user is admin
            if (["owner","admin"].includes(user?.role)) qc.invalidateQueries({ queryKey: ["admin-favourites"] });
        },
    });
    const clearMut = useMutation({
        mutationFn: ({ target_type, target_id }) =>
            api.delete("/favourites", { params: { target_type, target_id } }).then((r) => r.data),
        onMutate: async (vars) => {
            const prev = qc.getQueryData(["my-favourites", user?.id || null]);
            qc.setQueryData(["my-favourites", user?.id || null], (old = []) =>
                old.filter((f) => !(f.target_type === vars.target_type && f.target_id === vars.target_id))
            );
            return { prev };
        },
        onError: (_e, _v, ctx) => {
            if (ctx?.prev != null) qc.setQueryData(["my-favourites", user?.id || null], ctx.prev);
        },
        onSettled: () => {
            qc.invalidateQueries({ queryKey: ["my-favourites", user?.id || null] });
            if (["owner","admin"].includes(user?.role)) qc.invalidateQueries({ queryKey: ["admin-favourites"] });
        },
    });

    const value = useMemo(() => {
        const mineMap = {};
        for (const f of mine) mineMap[`${f.target_type}:${f.target_id}`] = f.tier;
        const adminMap = {};
        for (const f of adminFavs) adminMap[`${f.target_type}:${f.target_id}`] = f.tier;
        const getTier = (type, id) => mineMap[`${type}:${id}`] || null;
        const getAdminTier = (type, id) => adminMap[`${type}:${id}`] || null;
        const setTier = (type, id, tier) => setMut.mutate({ target_type: type, target_id: id, tier });
        const clearTier = (type, id) => clearMut.mutate({ target_type: type, target_id: id });
        return { mine, adminFavs, getTier, getAdminTier, setTier, clearTier, isAuthed };
    }, [mine, adminFavs, isAuthed, setMut, clearMut]);

    return <FavCtx.Provider value={value}>{children}</FavCtx.Provider>;
};

export const useFavourites = () => useContext(FavCtx);
